import { afterNextRender, Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';

import { filter, Observable, Subscription } from 'rxjs';
import { Select, Store } from '@ngxs/store';

import { environment } from '../../../environments/environment';
import { SystemStatistics } from '../../core/models';
import { BlogService } from '../../core/services';
import { voucherImage } from '../../core/helpers';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { SwiperContainer } from 'swiper/element';

@Component({
  selector: 'gs-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  @Select(state => state.staticData.systemStatistics) systemStatistics$: Observable<SystemStatistics>;
  @ViewChild('heroCarousel') heroCarouselRef!: ElementRef<SwiperContainer>;
  @ViewChild('partnerCarousel') partnerCarouselRef!: ElementRef<SwiperContainer>;
  @ViewChild('featuredMediaCarousel') featuredMediaCarouselRef!: ElementRef<SwiperContainer>;

  events: any[];
  blogPosts: any[];
  vouchers: any[];

  readonly partnerSlides: any[] = [
    { name: 'Altex', slug: undefined, image: undefined },
    { name: 'Kaufland Romania', slug: undefined, image: undefined },
    { name: 'H&M Romania', slug: undefined, image: undefined },
    { name: 'Therme Bucuresti', slug: undefined, image: undefined },
    { name: 'Decathlon Romania', slug: undefined, image: undefined },
    { name: 'Sephora Romania', slug: undefined, image: undefined },
    { name: 'Cărturești', slug: undefined, image: undefined },
    { name: 'Zalando', slug: undefined, image: undefined },
    { name: 'IKEA Romania', slug: undefined, image: undefined },
    { name: 'Media Galaxy', slug: undefined, image: undefined },
    { name: 'PRIMARK Romania', slug: undefined, image: undefined },
    { name: 'elefant.ro', slug: undefined, image: undefined },
    { name: 'Carrefour Romania', slug: undefined, image: undefined },
  ];

  readonly featuredMediaSlides = [
    { name: "Adevarul", image: "/assets/images/featured/adevarul.png", url: "https://adevarul.ro/economie/stiri-economice/ce-fac-romanii-cadourile-nepotrivite-nefolositoare-1_5b4f0552df52022f755cf165/index.html" },
    { name: "Antena 1", image: "/assets/images/featured/antena1.png", url: "https://www.youtube.com/watch?v=PEpFk1I25mA" },
    { name: "Business Magazin", image: "/assets/images/featured/business-magazin.png", url: "https://www.businessmagazin.ro/actualitate/giftshare-aproape-un-sfert-dintre-romani-prefera-sa-primeasca-bani-de-ziua-lor-pentru-a-si-alege-singuri-cadoul-17361508" },
    { name: "Forbes Romania", image: "/assets/images/featured/forbes-romania.png", url: "https://www.forbes.ro/sondaj-giftshare-cat-cheltuie-romanii-pentru-cadourile-de-craciun-131392" },
    { name: "PRO TV", image: "/assets/images/featured/protv.png", url: "https://stirileprotv.ro/stiri/ilikeit/iliket-cum-sa-alegem-cadoul-perfect-cu-ajutorul-unei-platforme-web-romanesti.html" },
    { name: "Ziarul Financiar", image: "/assets/images/featured/ziarul-financiar.png", url: "https://www.zf.ro/business-hi-tech/platforma-de-cadouri-giftshare-ro-mizeaza-pe-afaceri-de-5-mil-euro-pana-la-finalul-lui-2019-17359502" },
    { name: "TVR", image: "/assets/images/featured/tvr.png", url: "https://www.youtube.com/watch?v=ecaM4TxSmLM" },

    // Duplicated because of this issue https://github.com/nolimits4web/swiper/issues/7254
    { name: "Adevarul", image: "/assets/images/featured/adevarul.png", url: "https://adevarul.ro/economie/stiri-economice/ce-fac-romanii-cadourile-nepotrivite-nefolositoare-1_5b4f0552df52022f755cf165/index.html" },
    { name: "Antena 1", image: "/assets/images/featured/antena1.png", url: "https://www.youtube.com/watch?v=PEpFk1I25mA" },
    { name: "Business Magazin", image: "/assets/images/featured/business-magazin.png", url: "https://www.businessmagazin.ro/actualitate/giftshare-aproape-un-sfert-dintre-romani-prefera-sa-primeasca-bani-de-ziua-lor-pentru-a-si-alege-singuri-cadoul-17361508" },
    { name: "Forbes Romania", image: "/assets/images/featured/forbes-romania.png", url: "https://www.forbes.ro/sondaj-giftshare-cat-cheltuie-romanii-pentru-cadourile-de-craciun-131392" },
    { name: "PRO TV", image: "/assets/images/featured/protv.png", url: "https://stirileprotv.ro/stiri/ilikeit/iliket-cum-sa-alegem-cadoul-perfect-cu-ajutorul-unei-platforme-web-romanesti.html" },
    { name: "Ziarul Financiar", image: "/assets/images/featured/ziarul-financiar.png", url: "https://www.zf.ro/business-hi-tech/platforma-de-cadouri-giftshare-ro-mizeaza-pe-afaceri-de-5-mil-euro-pana-la-finalul-lui-2019-17359502" },
    { name: "TVR", image: "/assets/images/featured/tvr.png", url: "https://www.youtube.com/watch?v=ecaM4TxSmLM" },
  ];

  private subscriptions: Subscription[] = [];

  get blogUrl(): string {
    return environment.blogUrl;
  }

  get apiUrl(): string {
    return environment.apiUrl;
  }

  get isPlatformBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  get isPlatformServer(): boolean {
    return isPlatformServer(this.platformId);
  }

  constructor(
    private blogService: BlogService,
    private store: Store,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    afterNextRender((): void => {
      // When setup from HTML template the autoplay is not working when switching routes
      if (this.heroCarouselRef?.nativeElement) {
        Object.assign(this.heroCarouselRef.nativeElement, {
          effect: 'fade',
          loop: true,
          autoplay: {
            delay: 5000,
            disableOnInteraction: false
          }
        });
        this.heroCarouselRef.nativeElement.initialize();
      }

      if (this.partnerCarouselRef?.nativeElement) {
        Object.assign(this.partnerCarouselRef.nativeElement, {
          spaceBetween: 24,
          grabCursor: false,
          centeredSlides: true,
          loop: true,
          autoplay: {
            delay: 0,
            disableOnInteraction: false,
            reverseDirection: false
          },
          speed: 8500,
          breakpoints: {
            0: { slidesPerView: 2 },
            400: { slidesPerView: 3 },
            600: { slidesPerView: 4 },
            800: { slidesPerView: 5 },
            1200: { slidesPerView: 6 },
            1400: { slidesPerView: 7 },
            1600: { slidesPerView: 8 }
          }
        });
        this.partnerCarouselRef.nativeElement.initialize();
      }

      if (this.featuredMediaCarouselRef?.nativeElement) {
        Object.assign(this.featuredMediaCarouselRef.nativeElement, {
          spaceBetween: 24,
          grabCursor: false,
          centeredSlides: true,
          loop: true,
          autoplay: {
            delay: 0,
            disableOnInteraction: false,
            reverseDirection: true
          },
          speed: 8500,
          breakpoints: {
            0: { slidesPerView: 2 },
            400: { slidesPerView: 3 },
            600: { slidesPerView: 4 },
            800: { slidesPerView: 5 },
            1200: { slidesPerView: 6 },
            1400: { slidesPerView: 7 },
            1600: { slidesPerView: 8 }
          }
        });
        this.featuredMediaCarouselRef.nativeElement.initialize();
      }
    });
  }

  ngOnInit() {
    this.getRecentBlogPosts();

    this.subscriptions.push(
      this.store.select<any>(state => state.staticData.vouchers)
        .pipe(filter(x => !!x))
        .subscribe(response => {
          this.partnerSlides.forEach(x => {
            const voucher = response.find(y => y.partnerName.toLowerCase() == x.name.toLowerCase());
            if (voucher) {
              x.slug = voucher.slug;
              x.image = voucherImage(voucher.image);
            }
          });
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getBlogSummary(text: string): string {
    const content = text && text.replace(/<[^>]+>/g, ' ').trim();
    return content && content.length > 60 ? content.slice(0, 60) + '...' : content;
  }

  private getRecentBlogPosts() {
    this.subscriptions.push(
      this.blogService.getRecentPosts()
        .subscribe((data: any) => {
          this.blogPosts = data;
        })
    );
  }
}
